/**
 * QUIPWA — notes on the device (the real NotesRepository)
 *
 * Every operation writes the local `notes` table and queues a `note.push`
 * for the sync; nothing here waits for Drive. Ten saves of one note coalesce
 * into one upload (the outbox keys ops by note id).
 *
 * ```ts
 * const id = await repo.create('# Hola', { title: 'Hola', type: 'Quick Note' })
 * await repo.save(id, '# Hola\n\nmundo', { title: 'Hola', type: 'Quick Note' })
 * // …the next sync uploads it (NotesDriveSync.push)
 * ```
 */
import type { Outbox } from '../local/outbox'
import { MODULE_ONE_FILENAME } from './course-promotion'
import {
  COURSE_INDEX_FILENAME, computeBacklinks, computeUnlinkedMentions, joinNote, noteFilename, splitNote,
  type Backlink, type LinkableNote, type NoteContent, type NoteMetadata, type UnlinkedMention,
} from './note-model'
import type { CreateNoteOptions, NotesRepository } from './notes-repository'
import { fieldsPatch, type NoteRow, type NotesTable } from './notes-table'

/** Outbox op kind; payload `{ id }`. Handled by NotesDriveSync.push. */
export const NOTE_PUSH = 'note.push'

export interface LocalNotesDeps {
  table: NotesTable
  outbox: Pick<Outbox, 'enqueue' | 'remove'>
  /** Id for a new note: a reserved Drive id, else a `local-…` one. */
  nextId: () => Promise<string>
  /** After every local write (the sync engine debounces). */
  onWrite?: () => void
  now?: () => number
}

export function rowToMetadata(row: NoteRow, byId: ReadonlyMap<string, NoteRow>): NoteMetadata {
  const parent = row.parent_id ? byId.get(row.parent_id) : undefined
  const folder = row.folder_id ?? parent?.folder_id ?? undefined
  return {
    file_id: row.id,
    filename: row.filename,
    title: row.title,
    tags: JSON.parse(row.tags) as string[],
    domain: row.domain || undefined,
    status: row.status,
    type: row.type,
    parent_id: row.parent_id ?? undefined,
    drive_folder_id: folder ?? undefined,
    modified_at: Math.floor(row.modified_at / 1000),
    size_bytes: row.body.length,
    pending: row.dirty === 1,
  }
}

export function rowToLinkable(row: NoteRow): LinkableNote {
  return { id: row.id, title: row.title, type: row.type, body: row.body }
}

export class LocalNotesRepository implements NotesRepository {
  readonly table: NotesTable
  private readonly deps: LocalNotesDeps
  private readonly now: () => number
  private readonly listeners = new Set<() => void>()

  constructor(deps: LocalNotesDeps) {
    this.deps = deps
    this.table = deps.table
    this.now = deps.now ?? Date.now
  }

  // ── reads ─────────────────────────────────────────────────────────────────

  async list(): Promise<NoteMetadata[]> {
    const rows = await this.table.listVisible()
    const byId = new Map(rows.map((r) => [r.id, r]))
    return rows.map((r) => rowToMetadata(r, byId))
  }

  async read(id: string): Promise<NoteContent | null> {
    const row = await this.table.get(id)
    if (!row || row.deleted) return null
    const { content, frontmatter } = splitNote(row.body)
    return { file_id: row.id, filename: row.filename, content, frontmatter }
  }

  async backlinks(id: string): Promise<Backlink[]> {
    return computeBacklinks((await this.table.listVisible()).map(rowToLinkable), id)
  }

  async unlinkedMentions(title: string, excludeId: string): Promise<UnlinkedMention[]> {
    return computeUnlinkedMentions((await this.table.listVisible()).map(rowToLinkable), title, excludeId)
  }

  // ── writes ────────────────────────────────────────────────────────────────

  async save(id: string, content: string, frontmatter: Record<string, unknown>): Promise<void> {
    const row = await this.table.get(id)
    if (!row || row.deleted) throw new Error(`Nota no encontrada: ${id}`)

    // Last line of defence against a save that fires while the editor is
    // between notes: an empty body never silently replaces one with text.
    if (content.trim() === '' && splitNote(row.body).content.trim() !== '') {
      console.warn('[Notes] Refused to overwrite', id, 'with an empty body')
      return
    }

    const body = joinNote(content, frontmatter)
    if (body === row.body) return // nothing changed: no upload either
    await this.table.touch(id, { body, ...fieldsPatch(body, row.filename) }, this.now())
    await this.queue(id)
  }

  async create(content: string, frontmatter: Record<string, unknown>, options: CreateNoteOptions = {}): Promise<string> {
    const id = await this.deps.nextId()
    const body = joinNote(content, frontmatter)
    const fields = fieldsPatch(body, 'nota.md')
    const parent = options.parentId ? await this.table.get(options.parentId) : null
    if (options.parentId && (!parent || parent.type !== 'Long Course')) {
      throw new Error('Solo un Curso Largo puede tener módulos')
    }
    const filename = !parent && fields.type === 'Long Course' ? COURSE_INDEX_FILENAME : noteFilename(fields.title)

    await this.table.insert({
      id,
      drive_file_id: null,
      parent_id: parent?.id ?? null,
      folder_id: null,
      filename,
      ...fields,
      body,
      base: null,
      base_version: null,
      modified_at: this.now(),
      local_rev: 1,
      dirty: 1,
      deleted: 0,
    })
    await this.queue(id)
    return id
  }

  async remove(id: string): Promise<string[]> {
    const row = await this.table.get(id)
    if (!row || row.deleted) return []

    // A course's modules live inside its Drive folder: trashing the folder
    // takes them, so locally they simply go.
    const modules = row.type === 'Long Course' ? await this.table.children(id) : []
    for (const m of modules) await this.purge(m.id)

    if (row.drive_file_id || row.folder_id) {
      // On Drive: the push trashes it (recoverable for 30 days), then purges the row.
      await this.table.touch(id, { deleted: 1 }, this.now())
      await this.queue(id)
    } else {
      // Never uploaded: nothing to undo on Drive.
      await this.purge(id)
      this.emit()
      this.deps.onWrite?.()
    }
    return [id, ...modules.map((m) => m.id)]
  }

  async promoteToLongCourse(id: string): Promise<string> {
    const note = await this.table.get(id)
    if (!note || note.deleted) throw new Error('No se encontró la nota')
    if (note.type === 'Long Course') return note.id
    if (note.parent_id) throw new Error('Un módulo no puede convertirse en curso')

    // The note keeps its id and text and becomes module 1, so every [[link]]
    // to it still lands on the text it was written against; a new note
    // becomes the course around it.
    const courseId = await this.deps.nextId()
    const frontmatter = {
      title: note.title,
      type: 'Long Course',
      status: note.status || 'Pending',
      domain: note.domain,
      tags: JSON.parse(note.tags) as string[],
    }
    const body = joinNote(`# ${note.title}\n\n## Módulos\n\n- [[${note.title}]]\n`, frontmatter)
    await this.table.insert({
      id: courseId,
      drive_file_id: null,
      parent_id: null,
      folder_id: null,
      filename: COURSE_INDEX_FILENAME,
      ...fieldsPatch(body, COURSE_INDEX_FILENAME),
      body,
      base: null,
      base_version: null,
      modified_at: this.now(),
      local_rev: 1,
      dirty: 1,
      deleted: 0,
    })
    // Only the file is renamed (modules sort by filename); the title stays.
    await this.table.touch(id, { parent_id: courseId, filename: MODULE_ONE_FILENAME }, this.now())
    await this.deps.outbox.enqueue(NOTE_PUSH, courseId, { id: courseId })
    await this.queue(id)
    return courseId
  }

  // ── change feed ───────────────────────────────────────────────────────────

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** Tell the UI the table changed (also called by the sync after a pull). */
  notifyChanged(): void {
    this.emit()
  }

  private emit(): void {
    for (const l of this.listeners) l()
  }

  private async queue(id: string): Promise<void> {
    await this.deps.outbox.enqueue(NOTE_PUSH, id, { id })
    this.emit()
    this.deps.onWrite?.()
  }

  private async purge(id: string): Promise<void> {
    await this.table.remove(id)
    await this.deps.outbox.remove(NOTE_PUSH, id)
  }
}
